import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Table, Button, Modal, Badge, Form } from "react-bootstrap";
import "jspdf-autotable";
import api from "api";
import { useLocation, useNavigate } from "react-router-dom";
import { IconButton, Tooltip, makeStyles } from "@material-ui/core";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import "./ListReports.css";
import ReportFilters from "./ReportFilters";
import { generatePDFReport, generateDOCXReport } from "./exports";
import PageHeader from "components/layout/page-header/PageHeader";
import AIAssistantFab from "components/ai-assistant-fab";
import AppModal from "components/ui/AppModal/AppModal";

const useBackButtonStyles = makeStyles(theme => ({
  iconButton: {
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  }
}));

const ListReports = () => {
  const navigate = useNavigate();
  const backButtonClasses = useBackButtonStyles();
  const token = useSelector(state => state.auth.accessToken);
  const [filePairs, setFilePairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [aiOpenSignal, setAiOpenSignal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [modalDescription, setModalDescription] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [xResumoData, setXResumoData] = useState(null);
  const [annotatedImagesUrls, setAnnotatedImagesUrls] = useState([]);
  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    variant: "success",
    title: "",
    message: ""
  });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, pair: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const location = useLocation();
  const resolvedStateRef = React.useRef(null);
  if (!resolvedStateRef.current) {
    if (location.state) {
      resolvedStateRef.current = location.state;
    } else {
      // Fallback para reloads completos vindos do Gêmeo Digital
      const stored = sessionStorage.getItem("listReportsState");
      if (stored) {
        sessionStorage.removeItem("listReportsState");
        resolvedStateRef.current = JSON.parse(stored);
      } else {
        resolvedStateRef.current = {};
      }
    }
  }
  const { project, date, folder_path: folderPathFromState } = resolvedStateRef.current;
  const folderPath = folderPathFromState || `projetos/${project?.name}/${date}/relatorios`;

  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      try {
        const response = await api.post(
          "/reports/list_files",
          { folder_path: folderPath },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const files = response.data.files || [];
        const pairs = [];
        let xResumoUrl = null;

        // Excluir resumo.json, xresumo.json e pasta anotadas/ da lista de itens do usuário
        const imgExt = /\.(jpg|jpeg|png|webp|gif)$/i;
        const byBase = {};
        for (const f of files) {
          const url = f.url || "";
          if (url.includes("/anotadas/") || url.endsWith("resumo.json") || url.includes("xresumo.json")) continue;
          const name = url.split("/").pop()?.split("?")[0] || "";
          const base = name.replace(/\.[^.]+$/, "");
          if (!base) continue;
          byBase[base] = byBase[base] || {};
          if (imgExt.test(name)) byBase[base].imageUrl = url;
          else if (name.endsWith(".json")) byBase[base].jsonUrl = url;
        }

        for (const base of Object.keys(byBase).sort()) {
          const { imageUrl, jsonUrl } = byBase[base];
          if (!jsonUrl) continue;
          try {
            const jsonData = await fetch(`${jsonUrl}?t=${Date.now()}`).then(r => r.json());
            pairs.push({
              imageUrl: imageUrl || "",
              jsonName: jsonUrl.split("/").pop()?.split("?")[0] || "",
              title: jsonData.titulo || "Sem título",
              description: jsonData.descricao || "Sem descrição",
              action: jsonData.acao || "Sem ação",
              severity: jsonData.severidade || "Sem severidade",
              date: jsonData.data || date
            });
          } catch (e) {
            console.warn("Ignorando JSON inválido:", jsonUrl, e);
          }
        }

        xResumoUrl = files.find(f => f.url?.includes("xresumo.json"))?.url || null;

        // Buscar resumo.json (IA) e/ou xresumo.json para exportação
        let resumoData = null;
        const resumoFile = files.find(f => f.url && f.url.endsWith("resumo.json") && !f.url.includes("xresumo"));
        if (resumoFile?.url) {
          try {
            const r = await fetch(`${resumoFile.url}?t=${Date.now()}`);
            if (r.ok) resumoData = await r.json();
          } catch (err) {
            console.error("Erro ao buscar resumo.json:", err);
          }
        }
        if (xResumoUrl) {
          try {
            const xResumoResponse = await fetch(`${xResumoUrl}?t=${Date.now()}`);
            if (xResumoResponse.ok) {
              const xResumoData = await xResumoResponse.json();
              if (resumoData && resumoData.parecer) {
                setXResumoData({ ...xResumoData, parecer: resumoData.parecer, aspecto_solicitado: resumoData.aspecto_solicitado, data_analise: resumoData.data_analise });
              } else {
                setXResumoData(xResumoData);
              }
            }
          } catch (err) {
            console.error("Erro ao buscar xresumo.json:", err);
          }
        }
        if (resumoData && resumoData.parecer && !xResumoUrl) {
          setXResumoData(resumoData);
        }

        // Imagens anotadas (pasta anotadas/)
        const anotadas = files
          .filter(f => f.url && f.url.includes("/anotadas/") && /\.(jpg|jpeg|png|webp)$/i.test(f.url))
          .map(f => f.url)
          .sort();
        setAnnotatedImagesUrls(anotadas);

        setFilePairs(pairs);
      } catch (err) {
        setError("Não foi possível carregar os arquivos.");
      } finally {
        setLoading(false);
      }
    };
    fetchFiles();
  }, [token, project?.name, date, project?.type, folderPath]);

  const requestSort = key => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedItems = useMemo(() => {
    let sortableItems = [...filePairs];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filePairs, sortConfig]);

  const filteredItems = sortedItems.filter(
    item =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.severity.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  const handleImageClick = url => {
    setModalImage(url);
    setShowModal(true);
  };

  const handleDescriptionClick = description => {
    setModalDescription(description);
    setShowDescriptionModal(true);
  };

  const truncateText = (text, maxLength = 200) => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  const formatMarkdown = text => {
    if (!text) return "";

    return (
      text
        // Negrito: **texto** ou __texto__
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/__(.*?)__/g, "<strong>$1</strong>")
        // Itálico: *texto* ou _texto_
        .replace(/\*(.*?)\*/g, "<em>$1</em>")
        .replace(/_(.*?)_/g, "<em>$1</em>")
        // Código inline: `código`
        .replace(/`(.*?)`/g, "<code>$1</code>")
        // Quebras de linha
        .replace(/\n/g, "<br>")
    );
  };

  const formatSeverity = severity => {
    if (!severity) return "";
    return severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase();
  };

  const handleCloseModal = () => setShowModal(false);
  const handleCloseDescriptionModal = () => setShowDescriptionModal(false);

  const handleEdit = index => {
    setEditIndex(index);
    setEditedData({ ...filePairs[index] });
  };

  const handleDelete = pair => {
    if (!pair || deleteLoading || deleteConfirm.open) return;
    setDeleteConfirm({ open: true, pair });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.pair) return;

    const pair = deleteConfirm.pair;
    setDeleteConfirm({ open: false, pair: null });
    setDeleteLoading(true);

    try {
      const jsonName = pair.jsonName;
      const response = await api.post(
        "/reports/delete_report",
        { folder_path: folderPath, json_name: jsonName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "success") {
        setFilePairs(prev => prev.filter(p => p.jsonName !== jsonName));
        setFeedbackModal({
          open: true,
          variant: "success",
          title: "Excluído!",
          message: "O relatório foi excluído com sucesso."
        });
      } else {
        setFeedbackModal({
          open: true,
          variant: "error",
          title: "Erro ao excluir",
          message: response.data.message || "Falha ao excluir o relatório."
        });
      }
    } catch (error) {
      setFeedbackModal({
        open: true,
        variant: "error",
        title: "Erro ao excluir",
        message: error.response?.data?.message || "Falha ao excluir o relatório."
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleInputChange = (key, value) =>
    setEditedData(prev => ({ ...prev, [key]: value }));

  const handleSave = async index => {
    try {
      await api.post(
        "/reports/edit_report",
        {
          folder_path: folderPath,
          json_name: editedData.jsonName,
          title: editedData.title,
          description: editedData.description,
          action: editedData.action,
          severity: editedData.severity
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFeedbackModal({
        open: true,
        variant: "success",
        title: "Salvo!",
        message: "As alterações foram salvas com sucesso."
      });

      const updatedFilePairs = [...filePairs];
      updatedFilePairs[index] = editedData;
      setFilePairs(updatedFilePairs);
      setEditIndex(null);
      setError(null);
    } catch (error) {
      setFeedbackModal({
        open: true,
        variant: "error",
        title: "Erro",
        message:
          error.response?.data?.message ||
          "Não foi possível salvar as alterações."
      });
    }
  };

  const handlePDFExport = () => {
    navigate("/reports/preview", {
      state: { filePairs, project, date, xResumoData, annotatedImagesUrls, format: "pdf" }
    });
  };

  const handleDOCXExport = () => {
    navigate("/reports/preview", {
      state: { filePairs, project, date, xResumoData, annotatedImagesUrls, format: "docx" }
    });
  };

  if (loading)
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2">Carregando relatórios...</p>
      </div>
    );

  const backButton = (
    <Tooltip title="Voltar">
      <IconButton
        aria-label="Voltar"
        onClick={() => navigate("/projects", { state: { openProject: project?.name } })}
        size="small"
        className={backButtonClasses.iconButton}
      >
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
  );

  if (error) {
    return (
      <div className="d-flex flex-column h-100">
        <PageHeader
          title="Relatórios"
          subtitle="Visualize os detalhes dos relatórios gerados para o projeto."
          accent="Relatórios"
          leftAdornment={backButton}
        />
        <div className="d-flex justify-content-center align-items-center flex-grow-1">
          <div className="alert alert-danger mb-0">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100">
      <PageHeader
        title={project?.name ? `Relatório — ${project.name}` : "Relatório"}
        subtitle={date ? `Data: ${date}` : "Detalhes do relatório do projeto."}
        accent="Relatórios"
        leftAdornment={backButton}
      />
      <div className="reports-container flex-grow-1">

      <div className="table-controls">
        <div className="search-container">
          <ReportFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            project={project}
            date={date}
          />
        </div>
        <div className="table-export-buttons">
          <div className="export-buttons d-flex align-items-center gap-3">
            <div className="export-dropdown">
              <Button
                className="d-flex align-items-center export-dropdown__trigger"
                style={{
                  backgroundColor: "#035184",
                  borderColor: "#035184",
                  color: "white"
                }}
              >
                <i className="fas fa-download me-2"></i>
                Exportar
              </Button>
              <div className="export-dropdown__menu">
                <button
                  type="button"
                  className="export-dropdown__item"
                  onClick={handlePDFExport}
                >
                  <i className="fas fa-file-pdf me-2"></i>
                  PDF
                </button>
                <button
                  type="button"
                  className="export-dropdown__item"
                  onClick={handleDOCXExport}
                >
                  <i className="fas fa-file-word me-2"></i>
                  DOCX
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="table-container">
        <Table striped bordered hover responsive>
          <thead className="table-header">
            <tr>
              <th style={{ width: "15%" }}>Imagem</th>
              <th
                style={{ width: "20%", cursor: "pointer" }}
                onClick={() => requestSort("title")}
              >
                Título{" "}
                {sortConfig.key === "title" && (
                  <i
                    className={`fas fa-sort-${
                      sortConfig.direction === "asc" ? "up" : "down"
                    }`}
                  />
                )}
              </th>
              <th style={{ width: "35%" }}>Descrição</th>
              <th style={{ width: "15%" }}>Ação</th>
              <th
                style={{ width: "15%", cursor: "pointer" }}
                onClick={() => requestSort("severity")}
              >
                Severidade{" "}
                {sortConfig.key === "severity" && (
                  <i
                    className={`fas fa-sort-${
                      sortConfig.direction === "asc" ? "up" : "down"
                    }`}
                  />
                )}
              </th>
              <th style={{ width: "10%" }}>Edição</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((pair, index) => (
              <tr key={index}>
                <td>
                  <img
                    src={pair.imageUrl}
                    alt={`Relatório ${index + 1}`}
                    className="report-image"
                    onClick={() => handleImageClick(pair.imageUrl)}
                  />
                </td>
                {editIndex === index ? (
                  <>
                    <td>
                      <Form.Control
                        type="text"
                        value={editedData.title}
                        onChange={e =>
                          handleInputChange("title", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={editedData.description}
                        onChange={e =>
                          handleInputChange("description", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={editedData.action}
                        onChange={e =>
                          handleInputChange("action", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Group>
                        {["Alta", "Média", "Baixa"].map(opt => (
                          <Form.Check
                            key={opt.toLowerCase()}
                            type="radio"
                            label={opt}
                            name="severity"
                            id={`severity-${opt.toLowerCase()}`}
                            value={opt}
                            checked={editedData.severity === opt.toLowerCase()}
                            onChange={e =>
                              handleInputChange("severity", e.target.value)
                            }
                          />
                        ))}
                      </Form.Group>
                    </td>
                    <td>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleSave(index)}
                        className="me-1"
                      >
                        <i className="fas fa-save"></i>
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditIndex(null)}
                      >
                        <i className="fas fa-times"></i>
                      </Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{pair.title}</td>
                    <td
                      className="description-cell"
                      onClick={() => handleDescriptionClick(pair.description)}
                      style={{ cursor: "pointer" }}
                      title={pair.description}
                    >
                      {truncateText(pair.description)}
                    </td>
                    <td>{pair.action}</td>
                    <td>
                      <Badge
                        bg={
                          pair.severity.toLowerCase() === "alta"
                            ? "danger"
                            : pair.severity.toLowerCase() === "média"
                            ? "warning"
                            : "success"
                        }
                        className="severity-badge"
                      >
                        {formatSeverity(pair.severity)}
                      </Badge>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleEdit(index)}
                          title="Editar"
                          className="mb-1"
                        >
                          <i className="fas fa-edit text-primary"></i>
                        </Button>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleDelete(pair)}
                          title="Excluir"
                        >
                          <i className="fas fa-trash text-danger"></i>
                        </Button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {filteredItems.length > itemsPerPage && (
        <div className="pagination-container">
          <div className="pagination-info">
            Mostrando {indexOfFirstItem + 1}-
            {Math.min(indexOfLastItem, filteredItems.length)} de{" "}
            {filteredItems.length} itens
          </div>
          <nav aria-label="Navegação de páginas">
            <ul className="pagination pagination-sm">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  title="Primeira página"
                >
                  <i className="fas fa-angle-double-left"></i>
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => prev - 1)}
                  disabled={currentPage === 1}
                  title="Página anterior"
                >
                  <i className="fas fa-angle-left"></i>
                </button>
              </li>

              {/* Mostrar apenas algumas páginas ao redor da página atual */}
              {(() => {
                const totalPages = Math.ceil(
                  filteredItems.length / itemsPerPage
                );
                const pages = [];
                const maxVisiblePages = 5;

                let startPage = Math.max(
                  1,
                  currentPage - Math.floor(maxVisiblePages / 2)
                );
                let endPage = Math.min(
                  totalPages,
                  startPage + maxVisiblePages - 1
                );

                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }

                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }

                return pages.map(pageNum => (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      currentPage === pageNum ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                ));
              })()}

              <li
                className={`page-item ${
                  currentPage === Math.ceil(filteredItems.length / itemsPerPage)
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={
                    currentPage ===
                    Math.ceil(filteredItems.length / itemsPerPage)
                  }
                  title="Próxima página"
                >
                  <i className="fas fa-angle-right"></i>
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === Math.ceil(filteredItems.length / itemsPerPage)
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(
                      Math.ceil(filteredItems.length / itemsPerPage)
                    )
                  }
                  disabled={
                    currentPage ===
                    Math.ceil(filteredItems.length / itemsPerPage)
                  }
                  title="Última página"
                >
                  <i className="fas fa-angle-double-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Fim da área principal do relatório */}
      </div>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        className="image-modal modal-top"
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal-title-small">
            Visualização da Imagem
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img
            src={modalImage}
            alt="Imagem do relatório"
            className="img-fluid w-100"
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={showDescriptionModal}
        onHide={handleCloseDescriptionModal}
        size="lg"
        className="description-modal modal-top"
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal-title-small">
            Descrição Completa
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className="description-content"
            dangerouslySetInnerHTML={{
              __html: formatMarkdown(modalDescription)
            }}
          />
        </Modal.Body>
      </Modal>

      <AppModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, pair: null })}
        allowOutsideClick={true}
        variant="warning"
        title="Tem certeza?"
        message="Você não poderá reverter esta ação!"
        confirmText="Sim, excluir!"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ open: false, pair: null })}
      />

      <AppModal
        isOpen={deleteLoading}
        onClose={() => {}}
        allowOutsideClick={false}
        variant="loading"
        title="Excluindo..."
        message="Aguarde enquanto o relatório é removido."
      />

      <AppModal
        isOpen={feedbackModal.open}
        onClose={() => setFeedbackModal(m => ({ ...m, open: false }))}
        allowOutsideClick={true}
        variant={feedbackModal.variant}
        title={feedbackModal.title}
        message={feedbackModal.message}
        confirmText="OK"
        cancelText={null}
        onConfirm={() => setFeedbackModal(m => ({ ...m, open: false }))}
      />

      <AIAssistantFab folderPath={folderPath} token={token} />
    </div>
  );
};

export default ListReports;
